<script setup lang="ts">
import { DEFAULT_SEO, formatSeoTitle, getAbsoluteSeoUrl } from '@/constants/seo';

type AppError = {
  statusCode?: number;
  statusMessage?: string;
  message?: string;
};

const props = defineProps<{
  error: AppError;
}>();

const statusCode = computed(() => props.error.statusCode ?? 500);
const isNotFound = computed(() => statusCode.value === 404);
const title = computed(() => (isNotFound.value ? 'Page not found' : 'Tradel is temporarily unavailable'));
const description = computed(() =>
  isNotFound.value
    ? 'The page you are looking for is not available on Tradel.'
    : DEFAULT_SEO.description
);
const image = getAbsoluteSeoUrl(DEFAULT_SEO.imagePath);

useHead(() => ({
  title: title.value,
  titleTemplate: (titleChunk?: string) => formatSeoTitle(titleChunk),
  meta: [{ key: 'robots', name: 'robots', content: DEFAULT_SEO.robotsNoIndex }],
}));

useSeoMeta({
  description,
  ogTitle: computed(() => formatSeoTitle(title.value)),
  ogDescription: description,
  ogImage: image,
  twitterTitle: computed(() => formatSeoTitle(title.value)),
  twitterDescription: description,
  twitterImage: image,
  twitterCard: 'summary',
});

const goHome = () => clearError({ redirect: '/' });
</script>

<template>
  <main class="error-page">
    <a class="error-page__brand" href="/" aria-label="Tradel home">
      <img src="/logo.png" alt="" class="error-page__logo" />
      <span>Tradel</span>
    </a>

    <section class="error-page__content">
      <p class="error-page__code">{{ statusCode }}</p>
      <h1>{{ title }}</h1>
      <p>{{ description }}</p>

      <div class="error-page__actions">
        <button type="button" @click="goHome">Go home</button>
        <a href="/contact">Contact support</a>
      </div>
    </section>
  </main>
</template>

<style scoped lang="scss">
.error-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(24px, 7vw, 88px);
  background: #f7f8f5;
  color: #191919;
  font-family:
    Inter,
    Roboto,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

.error-page__brand {
  position: absolute;
  top: clamp(20px, 4vw, 40px);
  left: clamp(24px, 7vw, 88px);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #191919;
  font-size: 18px;
  font-weight: 700;
  text-decoration: none;
}

.error-page__logo {
  width: 40px;
  height: 40px;
}

.error-page__content {
  width: min(100%, 620px);
}

.error-page__code {
  margin: 0 0 12px;
  color: #f47c2f;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  font-size: clamp(40px, 7vw, 72px);
  line-height: 1;
  letter-spacing: 0;
}

.error-page__content > p:last-of-type {
  margin: 24px 0 0;
  color: #555f63;
  font-size: 18px;
  line-height: 1.6;
}

.error-page__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.error-page__actions button,
.error-page__actions a {
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  border: 1px solid #191919;
  border-radius: 6px;
  color: #191919;
  font: inherit;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
}

.error-page__actions button {
  background: #191919;
  color: #ffffff;
}

.error-page__actions a {
  background: transparent;
}

@media (max-width: 600px) {
  .error-page {
    justify-content: flex-start;
    padding-top: 128px;
  }

  .error-page__actions {
    flex-direction: column;
  }

  .error-page__actions button,
  .error-page__actions a {
    width: 100%;
  }
}
</style>
